import React from 'react';
import { XMarkIcon, CheckCircleIcon, XCircleIcon } from '@heroicons/react/24/outline';
import { AutomationApproval } from '../types/Automation';

interface AutomationApprovalModalProps {
  approval: AutomationApproval | null;
  onApprove: () => void;
  onReject: () => void;
  onClose: () => void;
  loading?: boolean;
}

const AutomationApprovalModal: React.FC<AutomationApprovalModalProps> = ({
  approval,
  onApprove,
  onReject,
  onClose,
  loading = false
}) => {
  if (!approval) return null;

  const formatDate = (dateString: string) => {
    try {
      return new Date(dateString).toLocaleString();
    } catch {
      return 'Invalid date';
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending':
        return 'text-yellow-600 bg-yellow-100';
      case 'approved':
        return 'text-green-600 bg-green-100';
      case 'rejected':
        return 'text-red-600 bg-red-100';
      default:
        return 'text-gray-600 bg-gray-100';
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center space-x-3">
            <div className="flex-shrink-0">
              <div className="w-10 h-10 bg-yellow-100 rounded-full flex items-center justify-center">
                <svg className="w-6 h-6 text-yellow-600" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
              </div>
            </div>
            <div>
              <h3 className="text-lg font-medium text-gray-900">
                Automation Permission Required
              </h3>
              <p className="text-sm text-gray-500">
                An automation rule is requesting permission to execute
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-4">
          {/* Rule Information */}
          <div className="bg-gray-50 rounded-lg p-4">
            <h4 className="font-medium text-gray-900 mb-2">Rule Details</h4>
            <div className="space-y-2 text-sm">
              <div>
                <span className="font-medium text-gray-700">Rule:</span>
                <span className="ml-2 text-gray-900">{approval.ruleName || 'Unknown Rule'}</span>
              </div>
              <div>
                <span className="font-medium text-gray-700">Triggered by:</span>
                <span className="ml-2 text-gray-900">{approval.triggeredByName || 'System'}</span>
              </div>
              <div>
                <span className="font-medium text-gray-700">Created:</span>
                <span className="ml-2 text-gray-900">{formatDate(approval.createdAt)}</span>
              </div>
            </div>
          </div>

          {/* Lead Information */}
          <div className="bg-blue-50 rounded-lg p-4">
            <h4 className="font-medium text-gray-900 mb-2">Lead Information</h4>
            <div className="space-y-2 text-sm">
              <div>
                <span className="font-medium text-gray-700">Lead:</span>
                <span className="ml-2 text-gray-900">
                  {approval.leadFirstName && approval.leadLastName 
                    ? `${approval.leadFirstName} ${approval.leadLastName}`
                    : 'Unknown Lead'
                  }
                </span>
              </div>
              {approval.leadEmail && (
                <div>
                  <span className="font-medium text-gray-700">Email:</span>
                  <span className="ml-2 text-gray-900">{approval.leadEmail}</span>
                </div>
              )}
            </div>
          </div>

          {/* Action Details */}
          <div className="bg-green-50 rounded-lg p-4">
            <h4 className="font-medium text-gray-900 mb-2">Proposed Action</h4>
            <div className="text-sm text-gray-700">
              {approval.approvalData?.taskName && (
                <div className="mb-2">
                  <span className="font-medium">Task Name:</span>
                  <span className="ml-2">{approval.approvalData.taskName}</span>
                </div>
              )}
              {approval.approvalData?.taskDescription && (
                <div className="mb-2">
                  <span className="font-medium">Description:</span>
                  <span className="ml-2">{approval.approvalData.taskDescription}</span>
                </div>
              )}
              {approval.approvalData?.priority && (
                <div className="mb-2">
                  <span className="font-medium">Priority:</span>
                  <span className="ml-2 capitalize">{approval.approvalData.priority}</span>
                </div>
              )}
              {approval.approvalData?.assigneeName && (
                <div>
                  <span className="font-medium">Assignee:</span>
                  <span className="ml-2">{approval.approvalData.assigneeName}</span>
                </div>
              )}
            </div>
          </div>

          {/* Expiration Warning */}
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
            <div className="flex items-center">
              <svg className="w-5 h-5 text-yellow-600 mr-2" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
              <div className="text-sm text-yellow-800">
                <span className="font-medium">Expires:</span>
                <span className="ml-1">{formatDate(approval.expiresAt)}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="flex items-center justify-end space-x-3 p-6 border-t border-gray-200 bg-gray-50">
          <button
            onClick={onReject}
            disabled={loading}
            className="flex items-center px-4 py-2 text-sm font-medium text-red-700 bg-red-100 border border-red-300 rounded-md hover:bg-red-200 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <XCircleIcon className="h-4 w-4 mr-2" />
            Reject
          </button>
          <button
            onClick={onApprove}
            disabled={loading}
            className="flex items-center px-4 py-2 text-sm font-medium text-white bg-green-600 border border-transparent rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <CheckCircleIcon className="h-4 w-4 mr-2" />
            Approve
          </button>
        </div>
      </div>
    </div>
  );
};

export default AutomationApprovalModal;
